import type { CoasterProject } from '../schema';

export const COASTERS_ASSERTION_AUDIENCE = 'buncha-coasters-api' as const;

export interface AnonymousViewer {
  kind: 'anonymous';
  id: null;
  displayName: 'Guest';
}

export interface AuthenticatedViewer {
  kind: 'authenticated';
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

export type CoasterViewer = AnonymousViewer | AuthenticatedViewer;

export type ProjectLifecycle = 'active' | 'archived';
export type ProjectVisibility = 'private' | 'public' | 'hidden';
export type RevisionModerationStatus = 'not_submitted' | 'pending' | 'approved' | 'rejected';

export interface OwnerProjectState {
  lifecycle: ProjectLifecycle;
  visibility: ProjectVisibility;
  workingRevisionStatus: RevisionModerationStatus;
}

export interface CloudDraftRecord {
  projectId: string;
  ownerId: string;
  version: number;
  project: CoasterProject;
  state: OwnerProjectState;
  pendingRevisionId: string | null;
  lastRejectedRevisionId: string | null;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  updatedAt: string;
}

export interface CloudDraftSummary {
  projectId: string;
  title: string;
  version: number;
  state: OwnerProjectState;
  pendingRevisionId: string | null;
  lastRejectedRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  updatedAt: string;
}

export interface SaveCloudDraftOptions {
  expectedVersion?: number | null;
}

export interface PublishRequest {
  projectId: string;
  expectedVersion: number;
  heroUpload?: HeroUploadDescriptor | null;
}

export interface PublishStatusRecord {
  projectId: string;
  state: OwnerProjectState;
  pendingRevisionId: string | null;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  rejectionReason: string | null;
  updatedAt: string;
}

export interface HeroUploadDescriptor {
  expiresAt: string;
  assets: {
    hero16x9: HeroAssetUploadDescriptor<1280, 720>;
    openGraph: HeroAssetUploadDescriptor<1200, 630>;
  };
}

export interface HeroAssetBundle {
  hero16x9: Blob;
  openGraph: Blob;
}

export interface HeroAssetUploadDescriptor<Width extends number, Height extends number> {
  uploadId: string;
  objectKey: string;
  uploadUrl: string;
  acceptedContentTypes: readonly ('image/webp' | 'image/png')[];
  maxBytes: number;
  requiredWidth: Width;
  requiredHeight: Height;
}

export interface PublicCoasterSummary {
  publicCoasterId: string;
  publicSlug: string;
  title: string;
  description: string;
  creator: PublicCreatorSummary;
  themeId: string;
  heroUrl: string | null;
  allowRemix: boolean;
  publishedAt: string;
}

export type PublicRideDefinitionV1 = Omit<
  CoasterProject,
  'projectId' | 'revisionId' | 'metadata' | 'permissions' | 'lineage'
> & {
  metadata: Pick<
    CoasterProject['metadata'],
    'title' | 'description' | 'createdAt' | 'updatedAt' | 'tags'
  >;
  permissions: Pick<CoasterProject['permissions'], 'allowRemix'>;
};

/** Anonymous wire payload. It must never contain authoring, owner, or revision identifiers. */
export interface PublicCoasterPayload extends PublicCoasterSummary {
  contentHash: string;
  definition: PublicRideDefinitionV1;
  lineage: PublicLineageSummary | null;
  intensity: PublicIntensitySummary;
  stats: PublicCoasterStats;
}

/** Client-only hydrated view used by the existing ride runtime. */
export interface PublicCoasterRecord extends PublicCoasterSummary {
  project: CoasterProject;
  contentHash: string;
  lineage: PublicLineageSummary | null;
  intensity: PublicIntensitySummary;
  stats: PublicCoasterStats;
}

export type PublicCatalogSort = 'most_boosted' | 'most_ridden' | 'newest';

export interface PublicCatalogQuery {
  sort: PublicCatalogSort;
  remixableOnly: boolean;
  creatorId?: string;
  cursor?: string;
  limit?: number;
}

export interface PublicCreatorSummary {
  publicCreatorId: string;
  displayName: string;
  avatarUrl: string | null;
  profilePath?: string | null;
  firstParty: boolean;
  deleted: boolean;
}

export interface PublicLineageSummary {
  sourcePublicCoasterId: string | null;
  sourceTitle: string | null;
  rootPublicCoasterId: string | null;
}

export interface PublicCoasterStats {
  qualifiedRides: number;
  boosts: number;
  remixes: number;
  favorites?: number;
}

export interface PublicCoasterViewerState {
  boosted: boolean;
  favorited: boolean;
  isOwner: boolean;
}

export interface PublicIntensitySummary {
  level: 'comfortable' | 'exciting' | 'intense' | 'extreme' | 'absurd';
  label: string;
}

/** Cacheable anonymous catalog card. */
export interface PublicCoasterCardData extends PublicCoasterSummary {
  intensity: PublicIntensitySummary;
  lineage: PublicLineageSummary | null;
  stats: PublicCoasterStats;
}

/** Client-only view model produced by merging a public card with no-store viewer state. */
export interface PublicCoasterCard extends PublicCoasterCardData {
  viewerState: PublicCoasterViewerState;
}

export interface PublicCatalogPage {
  items: readonly PublicCoasterCardData[];
  nextCursor: string | null;
  sort: PublicCatalogSort;
  rankingWindowLabel: string | null;
  generatedAt: string;
}

export interface DiscoveryCatalogPage extends Omit<PublicCatalogPage, 'items'> {
  items: readonly PublicCoasterCard[];
}

export interface PublicCoasterViewerStateBatch {
  items: Readonly<Record<string, PublicCoasterViewerState>>;
}

export type CreatorMilestoneId =
  'first_ride' | 'ten_rides' | 'first_boost' | 'ten_boosts' | 'first_remix' | 'first_referred_ride';

export interface CreatorMilestone {
  id: CreatorMilestoneId;
  label: string;
  unlockedAt: string;
}

export interface SocialMutationRequest {
  publicCoasterId: string;
  desired: boolean;
  idempotencyKey: string;
}

export interface SocialMutationResult {
  publicCoasterId: string;
  boosted: boolean;
  favorited: boolean;
  authoritativeCounts: PublicCoasterStats;
  newMilestones: readonly CreatorMilestone[];
}

export interface MyCoasterSummary extends CloudDraftSummary {
  heroUrl: string | null;
  intensityLabel: string | null;
  stats: PublicCoasterStats | null;
}

export interface ReferralLinkResult {
  referralId: string;
  canonicalPath: string;
  expiresAt: string;
}

export interface QualifiedRideStartRequest {
  referralId?: string;
}

export interface QualifiedRideStartResult {
  rideSessionId: string;
  startedAt: string;
}

export interface QualifiedRideCompletionRequest {
  rideSessionId: string;
}

export interface QualifiedRideCompletionResult {
  qualified: boolean;
  counted: boolean;
  completedAt: string;
}

export interface OpaquePlayContext {
  token: string;
}

export interface PlayContextProvider {
  getPlayContext(): Promise<OpaquePlayContext | null>;
}

export interface ReadOnlyShare {
  shareId: string;
  secret: string;
  path: string;
  projectId: string;
  revisionId: string;
  createdAt: string;
}

export interface ReadOnlyShareRecord {
  shareId: string;
  projectId: string;
  revisionId: string;
  title: string;
  project: CoasterProject;
  allowRemix: boolean;
}

export interface RemixRequest {
  publicCoasterId?: string;
  share?: { shareId: string; secret: string };
}

export interface RemixResult {
  draft: CloudDraftRecord;
  lineage: NonNullable<CoasterProject['lineage']>;
}

export interface BunchaProfileResult {
  available: boolean;
  player?: { id: string; displayName: string; avatarUrl?: string | null };
  reason?: string;
}

export interface BunchaAssertionResult {
  available: boolean;
  token?: string;
  expiresAt?: string;
  reason?: string;
}

export interface BunchaPlayerApi {
  getProfile(): Promise<BunchaProfileResult>;
  getAssertion(request: {
    audience: typeof COASTERS_ASSERTION_AUDIENCE;
  }): Promise<BunchaAssertionResult>;
}

export interface BunchaWindowApi {
  player?: BunchaPlayerApi;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

declare global {
  interface Window {
    buncha?: BunchaWindowApi;
  }
}
